import { Injectable, inject, effect, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Store } from '@ngrx/store';
import { Auth0Service } from '../../core/auth/services/auth0.service';
import { selectCurrentDomainId } from '../../store/entities/domains/domains.selectors';
import * as DomainsActions from '../../store/entities/domains/domains.actions';
import { distinctUntilChanged, filter, take } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class ReactAppBridgeService {
  private auth0Service = inject(Auth0Service);
  private store = inject(Store);
  private platformId = inject(PLATFORM_ID);
  private isReactAppReady = false;
  private reactDomainChangeHandler: ((event: Event) => void) | null = null;
  /** Tracks the last domain ID received from React to prevent echo loops. */
  private lastReactInitiatedDomainId: string | null = null;

  constructor() {
    effect(() => {
      this.syncAuthStateToReact();
    });
  }

  /**
   * Reads auth signals and dispatches state to the React app.
   * NOTE: All signal reads MUST happen unconditionally (after the early
   * return) so that Angular's effect() tracks them and re-fires on changes.
   */
  private syncAuthStateToReact(): void {
    if (!this.isReactAppReady) {
      return;
    }

    const isAuthenticated = this.auth0Service.isAuthenticated();
    const user = this.auth0Service.user();
    const accessToken = this.auth0Service.accessToken();
    const isLoading = this.auth0Service.isLoading();

    this.notifyReactAuthStateChange({
      isAuthenticated,
      user,
      accessToken,
      isLoading,
    }).catch(err => {
      console.warn('Failed to notify React of auth state change:', err);
    });

    this.store.select(selectCurrentDomainId)
      .pipe(distinctUntilChanged())
      .subscribe(domainId => {
        if (this.isReactAppReady && domainId) {
          // Skip the echo: if this domain change was triggered by React itself,
          // don't bounce it back as an angular-domain-change event.
          if (domainId === this.lastReactInitiatedDomainId) {
            this.lastReactInitiatedDomainId = null;

            return;
          }
          this.notifyReactDomainChange(domainId);
        }
      });

    this.listenForReactDomainChange();
  }

  enableReactNotifications(): void {
    this.isReactAppReady = true;
    this.dispatchInitialDomainState();
  }

  private dispatchInitialDomainState(): void {
    this.store.select(selectCurrentDomainId).pipe(
      take(1),
      filter((domainId): domainId is string => Boolean(domainId))
    ).subscribe(domainId => {
      this.notifyReactDomainChange(domainId);
    });
  }

  async notifyReactAuthStateChange(authState: {
    isAuthenticated: boolean;
    user: any | null;
    accessToken: string | null;
    isLoading: boolean;
  }): Promise<void> {
    if (typeof window === 'undefined') {
      return;
    }

    if ((window as any).__ANGULAR_ENV__) {
      (window as any).__ANGULAR_ENV__.auth0State = authState;
    }

    if (this.isReactAppReady) {
      window.dispatchEvent(new CustomEvent('angular-auth0-state-change', {
        detail: authState,
      }));
    }
  }

  async dispatchInitialAuthState(): Promise<void> {
    if (typeof window === 'undefined' || !this.isReactAppReady) {
      return;
    }

    const authState = await this.auth0Service.getAuthStateForReact();

    this.notifyReactAuthStateChange(authState);
  }

  notifyReactDomainChange(domainId: string): void {
    if (typeof window === 'undefined' || !this.isReactAppReady) {
      return;
    }

    localStorage.setItem('domainID', domainId);

    window.dispatchEvent(new CustomEvent('angular-domain-change', {
      detail: { domainId },
    }));
  }

  /**
   * Listens for domain changes originating from the React app (e.g. the React
   * domain selector) and syncs them back into the Angular NgRx store.
   *
   * When React switches domain it only updates its own Redux store. This
   * handler bridges that event into `setCurrentDomainId`, which triggers the
   * `DomainChangeCoordinatorEffects` that reloads features — including the
   * `use-react-ui` flag that controls which shell (Angular vs React) is shown.
   */
  private listenForReactDomainChange(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    this.reactDomainChangeHandler = (event: Event) => {
      const { domainId } = (event as CustomEvent<{ domainId: string }>).detail;

      if (domainId) {
        this.lastReactInitiatedDomainId = domainId;
        this.store.dispatch(DomainsActions.setCurrentDomainId({ domainId }));
      }
    };

    window.addEventListener('react-domain-change', this.reactDomainChangeHandler);
  }
}

